import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getPromotionCandidates, type PromotionCandidate } from '../services/memory-api.js';
import { router } from '../services/router.js';

type PipelineStatus = 'curator_approved' | 'pending_curator' | 'user_confirmed' | 'user_rejected' | 'distributed';

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

const STATUS_LABELS: Record<string, string> = {
  pending_curator: 'Warte auf Curator',
  curator_approved: 'Curator OK',
  curator_rejected: 'Curator Abgelehnt',
  user_confirmed: 'Bestätigt',
  user_rejected: 'Abgelehnt',
  distributed: 'Verteilt',
};

@customElement('cp-skill-promotion-pipeline')
export class CpSkillPromotionPipeline extends LitElement {
  createRenderRoot() { return this; }

  @state() private _status: PipelineStatus = 'curator_approved';
  @state() private _candidates: PromotionCandidate[] = [];
  @state() private _loading = false;
  @state() private _error = '';

  connectedCallback(): void {
    super.connectedCallback();
    void this._load();
  }

  private async _load(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      const res = await getPromotionCandidates(this._status);
      this._candidates = res.candidates;
    } catch (e) {
      this._error = (e as Error).message;
    } finally {
      this._loading = false;
    }
  }

  private _switchStatus(s: PipelineStatus): void {
    this._status = s;
    void this._load();
  }

  private _openCandidate(id: number): void {
    router.navigateTo(`/skills/promotion/${id}`);
  }

  render() {
    const statuses: PipelineStatus[] = [
      'curator_approved', 'pending_curator', 'user_confirmed', 'user_rejected', 'distributed',
    ];
    return html`
      <div class="cp-skill-promotion-pipeline">
        <div class="cp-skill-promotion-pipeline__header">
          <h4 class="cp-skill-promotion-pipeline__title">Skill-Promotion-Pipeline</h4>
          <div class="cp-skill-promotion-pipeline__tabs">
            ${statuses.map(s => html`
              <button
                class="cp-skill-promotion-pipeline__tab ${this._status === s ? 'cp-skill-promotion-pipeline__tab--active' : ''}"
                @click=${() => this._switchStatus(s)}
              >${STATUS_LABELS[s] ?? s}</button>
            `)}
          </div>
        </div>

        ${this._error
          ? html`<div class="cp-skill-promotion-pipeline__error">${this._error}</div>`
          : nothing}

        ${this._loading
          ? html`<div class="cp-skill-promotion-pipeline__loading">Laedt…</div>`
          : this._renderList()}
      </div>
    `;
  }

  private _renderList() {
    if (this._candidates.length === 0) {
      return html`<div class="cp-skill-promotion-pipeline__empty">Keine Kandidaten mit Status "${STATUS_LABELS[this._status] ?? this._status}".</div>`;
    }
    return html`
      <div class="cp-skill-promotion-pipeline__list">
        ${this._candidates.map(c => html`
          <div
            class="cp-skill-promotion-pipeline__card"
            role="button"
            tabindex="0"
            @click=${() => this._openCandidate(c.id)}
            @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this._openCandidate(c.id); }}
          >
            <div class="cp-skill-promotion-pipeline__card-title">
              ${c.proposed_skill_title ?? c.pattern_tag}
            </div>
            <div class="cp-skill-promotion-pipeline__card-meta">
              <span>${c.cluster_count} Memories</span>
              <span>${fmtDate(c.created_at)}</span>
              ${c.curator_decision_reason
                ? html`<span class="cp-skill-promotion-pipeline__reason">${c.curator_decision_reason}</span>`
                : nothing}
            </div>
          </div>
        `)}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-skill-promotion-pipeline': CpSkillPromotionPipeline;
  }
}
